import { createTheme, responsiveFontSizes } from "@mui/material";
import type { CSSProperties } from "react";

// The dense text variants declared in `typography` below, made known to `<Typography variant>`
// and to `theme.typography.*`. Augmentations have to live in a module the app imports, and the
// theme is imported by every provider tree (and the theme tests), so this is the one place.
declare module "@mui/material/styles" {
	interface TypographyVariants {
		dense: CSSProperties;
		label: CSSProperties;
		micro: CSSProperties;
	}
	interface TypographyVariantsOptions {
		dense?: CSSProperties;
		label?: CSSProperties;
		micro?: CSSProperties;
	}
}
declare module "@mui/material/Typography" {
	interface TypographyPropsVariantOverrides {
		dense: true;
		label: true;
		micro: true;
	}
}
// `billing`: the one hue the job-status scale needs that MUI's semantic slots do not have (see
// `getJobStatusChipStyle`). Declared as a full palette color so `<Chip color="billing">` works.
declare module "@mui/material/styles" {
	interface Palette {
		billing: Palette["primary"];
	}
	interface PaletteOptions {
		billing?: PaletteOptions["primary"];
	}
}
declare module "@mui/material/Chip" {
	interface ChipPropsColorOverrides {
		billing: true;
	}
}
// The app's named surfaces beyond MUI's `elevation` and `outlined` (docs/design-system.md §3).
// `Card` renders a `Paper`, so `<Card variant="floating">` works too.
declare module "@mui/material/Paper" {
	interface PaperPropsVariantOverrides {
		floating: true;
		inset: true;
	}
}

/**
 * Application theme — the single source of truth for design tokens.
 *
 * `cssVariables: { colorSchemeSelector: "class" }` (MUI v6) makes `createTheme` emit
 * `--mui-palette-*` (and typography/spacing) CSS custom properties AND a light + dark
 * variant of every palette var, toggled by a `.light` / `.dark` class on `<html>`. The
 * ~16 component CSS files consume those generated vars directly, so most surfaces switch
 * schemes for free (this is what retired the hand-maintained `tokens.css`; design-audit §3, §8).
 *
 * Dark mode (customer-portal-qdc5): the user's Light/Dark/System choice is driven entirely
 * by MUI's own `useColorScheme()` hook (localStorage key `mui-mode`, matchMedia system-
 * following). MUI v6.5 merged `CssVarsProvider` into the plain `ThemeProvider`, so the single
 * `<ThemeProvider theme={theme}>` in AppProviders is all that's needed — do NOT add a
 * `CssVarsProvider` (a v5-era reflex). Flash-of-wrong-theme on first paint is prevented by the
 * static `InitColorSchemeScript` markup in `Web/index.html` (a mounted React component can't
 * pre-empt first paint) — its `attribute="class"` must stay in sync with `colorSchemeSelector`.
 *
 * `colorSchemes.light.palette` is a VERBATIM copy of the pre-dark-mode palette, so light mode
 * is visually unchanged. The semantic slots (success/warning/error/info) match the hexes
 * already in use (design-audit §2.2/§2.4). `colorSchemes.dark.palette` is a DESIGNED dark
 * palette (not a naive inversion): only the values that must change per scheme are overridden;
 * text/divider/action inherit MUI's dark defaults.
 *
 * NOTE: only `palette` (and `shadows`) may vary per scheme — `typography`, `spacing`, `shape`,
 * and `components` stay top-level.
 *
 * This module is intentionally free of app/provider imports so it can be unit-tested in
 * isolation (AppProviders.tsx pulls the whole component graph, incl. the Toolbar deep-icon
 * import that poisons the vitest transform).
 */

// Brand colors are identical across schemes (navy AppBar reads consistently in both) — kept as
// consts so "same value in both schemes" is structural, not just a prose comment.
const BRAND_PRIMARY = "#274052";
const BRAND_SECONDARY = "#dc004e";

const baseTheme = createTheme({
	cssVariables: { colorSchemeSelector: "class" },
	colorSchemes: {
		light: {
			palette: {
				primary: {
					main: BRAND_PRIMARY,
				},
				secondary: {
					main: BRAND_SECONDARY,
				},
				// Semantic slots — values match colors already in use (design-audit §2.4).
				error: {
					main: "#d32f2f", // == former --color-error / MUI default error.main
					dark: "#b71c1c", // == ERROR_STROKE (OpsMap.layers)
				},
				warning: {
					main: "#ed6c02", // "// warning.main" annotation in DealCard
					light: "#ff9800", // age-status / map warning orange
				},
				success: {
					main: "#4caf50", // #4caf50 (12 occ) → success.main per §2.4
					dark: "#2e7d32", // "// success.dark" annotation in DealCard
					light: "#66bb6a",
				},
				info: {
					main: "#1976d2", // UI "current/active" blue accent (RobotList, §2.2)
				},
				// Billing statuses (Approved, Invoiced). A saturated violet, after Airtable's own
				// purple for those options. MUI only derives shades for its built-in slots, so a
				// custom color states all four.
				billing: { main: "#7c3aed", light: "#a78bfa", dark: "#5b21b6", contrastText: "#fff" },
			},
		},
		dark: {
			palette: {
				// The dark scheme needs a LIGHTER primary: brand navy (#274052) is near-invisible
				// as outlined/text buttons, chips, and links on dark surfaces (verification pass
				// found exactly that across the wizard and Job Overview). Same hue family, lifted
				// for contrast. The AppBar is unaffected: MUI's AppBar uses its dark paper
				// background in dark mode (enableColorOnDark defaults to false), and in light mode
				// primary.main is still brand navy. Brand SURFACES that hardcode white text (the
				// Home cards) pin navy directly instead of using this slot.
				primary: {
					main: "#7fa8c9",
					// Explicit hover shade: MUI's auto-derived dark (#58758C) drops contained-
					// button hover text to 3.97:1 with the auto contrastText (adversarial-review
					// finding). #5d87ab keeps >=5.5:1 with the dark contrastText.
					dark: "#5d87ab",
				},
				secondary: {
					// Lifted for dark: brand crimson (#dc004e) measures 3.33:1 as text on dark
					// rows (dashboard group headers read as an error state). Same-hue pink-200.
					main: "#f48fb1",
				},
				// Semantic mains carry over; MUI's dark scheme derives readable light/dark
				// shades. Overrides added only where a contrast check fails.
				error: {
					main: "#f44336", // MUI dark-scheme error.main (brighter for dark bg)
					dark: "#d32f2f",
				},
				warning: {
					main: "#ffa726", // MUI dark-scheme warning.main
				},
				success: {
					main: "#66bb6a", // MUI dark-scheme success.main (lighter for dark bg)
				},
				info: {
					main: "#42a5f5", // MUI dark-scheme info.main
				},
				// Lifted for dark surfaces but kept saturated: white text measures 4.6:1 on it.
				billing: { main: "#8b5cf6", light: "#c4b5fd", dark: "#6d28d9", contrastText: "#fff" },
				// Disabled emphasis, slightly raised from MUI's dark defaults (0.3/0.12):
				// several pages' PRIMARY call-to-action starts disabled (wizard NEXT, App Logs
				// downloads, Generate Password) and measured 2.5:1 — ghosts users can't see.
				action: {
					disabled: "rgba(255, 255, 255, 0.45)",
					disabledBackground: "rgba(255, 255, 255, 0.16)",
				},
				// Designed dark surfaces: paper slightly elevated above the base so cards /
				// dialogs / the drawer read as distinct layers. text/divider inherit MUI dark
				// defaults (rgba-white ramps), which the chrome CSS consumes via vars.
				background: {
					default: "#121212",
					paper: "#1e1e1e",
				},
			},
		},
	},
	// Typography — anchor the family/weights so a scale exists to lean on. Variant
	// sizes stay at MUI defaults (appearance-preserving); responsiveFontSizes below
	// folds in the mobile-heading scaling the app currently fakes with inline ternaries.
	typography: {
		fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
		fontWeightRegular: 400,
		fontWeightMedium: 500,
		fontWeightBold: 700,
		// Dense variants (design-audit follow-up). The app sets sub-body sizes inline ~350
		// times, overwhelmingly five values layered on `caption`/`body2`: an unofficial scale
		// living in the call sites. These name the three sizes that stay legible so new text
		// reaches for a variant instead of a literal (check-structure.sh ratchets the literal
		// count). 0.7rem (11.2px) is the floor: the 0.65/0.6/0.55rem sites migrate UP to
		// `micro`. Not in MUI's variant→element map, so they render as <span>, inline-safe
		// inside any block; pass `component="p"`/`"div"` for a block.
		dense: { fontSize: "0.8rem", lineHeight: 1.4 }, // dense panel/body text (was 0.8rem × 65)
		label: { fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.3 }, // field/column labels (0.75rem × 54)
		micro: { fontSize: "0.7rem", lineHeight: 1.3 }, // chips, pills, footnotes (0.7rem × 99 + the smaller ones)
	},
	// Spacing — MUI's default 8px scale; the app already uses it implicitly via `sx`
	// numbers (design-audit §4). Stated explicitly to make it a deliberate decision.
	spacing: 8,
	// Shape — 4px is MUI's default and the app's most common rendered radius
	// (buttons/inputs/selects; design-audit §6). Held constant to avoid a restyle.
	shape: {
		borderRadius: 4,
	},
	components: {
		// The app is a pure client-rendered SPA with 36+ useMediaQuery call sites
		// (design-audit §3). noSsr skips the first-paint pass where every query
		// resolves false, removing a layout flash. Final render is identical.
		MuiUseMediaQuery: {
			defaultProps: { noSsr: true },
		},
		// Real keyboard-focus ring. MUI's default focusVisible cue is a ~12%-alpha overlay,
		// which is indistinguishable on dark surfaces (WCAG 2.4.7 finding from the dark-mode
		// adversarial review). Keyboard-only (Mui-focusVisible), so pointer clicks are
		// unaffected; primary var resolves per scheme.
		MuiButtonBase: {
			styleOverrides: {
				root: {
					"&.Mui-focusVisible": {
						outline: "2px solid var(--mui-palette-primary-main)",
						outlineOffset: "2px",
					},
				},
			},
		},
		// Badge numerals: white 12px on error red #f44336 measures 3.68:1. Darken the dark-
		// scheme badge fill to keep white text >=6:1; light mode unchanged.
		MuiBadge: {
			styleOverrides: {
				colorError: ({ theme: t }) => ({
					...t.applyStyles("dark", { backgroundColor: "#b71c1c" }),
				}),
			},
		},
		// The app's one tooltip look, formerly PortalTooltip.css (16 `!important` rules fighting
		// MUI's own styles). Owning it here styles every Tooltip, the ~11 plain MUI ones included,
		// the same way. grey.700 is a static-scale grey, so white text reads in both schemes
		// without a per-scheme override. `.portal-tooltip-anchor` is the popper class
		// PortalTooltip's `align="anchor"` sets: a uniform gap from the trigger in place of MUI's
		// per-placement margins.
		MuiTooltip: {
			styleOverrides: {
				tooltip: ({ theme: t }) => ({
					backgroundColor: t.palette.grey[700],
					color: t.palette.common.white,
					fontSize: "0.875rem",
					lineHeight: 1.4,
					padding: "12px 16px",
					borderRadius: 8,
					maxWidth: 300,
					boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
					whiteSpace: "pre-line",
					wordBreak: "break-word",
				}),
				arrow: ({ theme: t }) => ({
					color: t.palette.grey[700],
					"&::before": { boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)" },
				}),
				popper: {
					"&.portal-tooltip-anchor .MuiTooltip-tooltip": { margin: 15 },
				},
			},
		},
		// Outlined is the app-wide text-field convention and MUI's default — anchored
		// here so it is a decision, not an accident (design-audit §6.1). No-op today.
		MuiTextField: {
			defaultProps: { variant: "outlined" },
		},
		// Status chips are used at size="small" across the app (JobOverviewCard,
		// flag chips, DealPropertyChip; design-audit §3). Making it the default lets
		// those call sites drop the repeated prop over time.
		MuiChip: {
			defaultProps: { size: "small" },
		},
		// Named surfaces (docs/design-system.md §3). Custom variants skip MUI's elevation styling,
		// so neither picks up the dark scheme's elevation overlay: `floating` is plain
		// `background.paper`, exactly what the map overlays hand-rolled before.
		MuiPaper: {
			variants: [
				{
					// A panel over a map: legend, overlay, control card.
					props: { variant: "floating" },
					style: ({ theme: t }) => ({ boxShadow: t.shadows[1] }),
				},
				{
					// A recessed block inside a dialog or detail pane. action.hover is a scheme-aware
					// alpha tint that reads as recessed over any ground; a host whose ground needs a
					// different fill sets `--Paper-inset-bg` on an ancestor (the dashboard's Job Detail
					// modal does, in dark mode).
					props: { variant: "inset" },
					style: {
						backgroundColor: "var(--Paper-inset-bg, var(--mui-palette-action-hover))",
						borderRadius: 8,
					},
				},
			],
		},
	},
});

export const theme = responsiveFontSizes(baseTheme);
